<template>
  <CustomModal
    class="set-main-image-modal"
    :is-modal-open="isOpen"
    @toggleModal="closeModal"
    @onClickAway="closeModal"
    :overlay="'dark'"
    :with-header="true"
    :with-footer="true"
    :custom-classes="'tw:w-[100vw] tw:md:max-w-[406px] tw:md:w-[406px] tw:h-screen tw:md:h-auto tw:md:rounded-md tw:rounded-none'"
  >
    <template #modalHeader>
      <p class="tw:text-xl tw:text-gray-800 tw:font-bold">{{ $t('app_images_set_as_main_image') }}</p>
    </template>

    <div class="tw:flex tw:flex-col tw:w-full">
      <p class="tw:mb-4">{{ $t('set_main_image_subtext') }}</p>
      <div>
        <div
          v-if="imageLoading"
          class="tw:absolute tw:inset-0 tw:flex tw:items-center tw:justify-center tw:bg-gray-100 tw:rounded-xl"
        >
          <SmallDotLoader />
        </div>

        <img
          :src="`https://imgcy.trivago.com/c_fill,d_dummy.jpeg,e_sharpen:60,f_auto,h_230,q_auto,w_358/${imageToSetAsMain?.imagePath}`"
          :alt="$t('set_main_image_subtext')"
          class="tw:rounded-xl tw:mb-6"
          @load="imageLoading = false"
          @error="imageLoading = false"
        />
      </div>

      <div>
        <h4 class="tw:font-bold tw:text-sm tw:mb-2">{{ $t('tbs_main_image_guidelines_header') }}</h4>
        <ul class="tw:flex tw:flex-col tw:gap-y-[2px]">
          <li v-for="(guideline, index) in guidelines" :key="`guideline_${index}`" class="tw:text-sm">
            <span
              :class="guideline.isPositive ? 'tw:text-blue-700' : 'tw:text-red-700'"
              class="tw:inline-block tw:w-2 tw:mr-1"
              >{{ guideline.isPositive ? '+ ' : '- ' }}</span
            >
            <span>{{ $t(guideline.label) }}</span>
          </li>
        </ul>
        <div class="tw:mt-2">
          <p class="tw:text-gray-600 tw:text-xs">{{ $t('main_image_footnote') }}</p>
        </div>
      </div>
    </div>

    <template #modalFooter>
      <div class="tw:w-full tw:flex tw:justify-end tw:gap-4">
        <GhostButton size="medium" class="tw:px-9" @click="closeModal">{{ $t('close') }}</GhostButton>
        <MainButton size="medium" class="tw:px-9" @click="mainImage">
          <span v-if="!isSettingMainImage">
            {{ $t('hotel_connect_onboarding_confirm_cta') }}
          </span>
          <span v-if="isSettingMainImage">
            <SmallDotLoader color="#fff" />
          </span>
        </MainButton>
      </div>
    </template>
  </CustomModal>
</template>
<script setup>
import { computed, ref, watch } from 'vue'
import { useStore } from 'vuex'

import CustomModal from '@/components/base/CustomModal.vue'
import MainButton from '@/components/base/buttons/MainButton.vue'
import GhostButton from '@/components/base/buttons/GhostButton.vue'
import SmallDotLoader from '@/components/base/loaders/SmallDotLoader.vue'

import useToastNotifications from '@/components/notifications/useToastNotifications'
import { usePropertyImages } from '@/components/propertyDetails/propertyImages/usePropertyImages.ts'
import { useSetMainImage } from '../queries'
import { selectedAccommodationId } from '@/layouts/queries'

import appAnalytics from '@/utils/tracking'

const store = useStore()
const emit = defineEmits(['closeModal'])
const closeModal = () => emit('closeModal')
const selectedOrganisationId = computed(() => store.state.session.selectedOrganisation?.id)
const { setMainImage } = useSetMainImage()
const { setIsSettingMainImage, isSettingMainImage } = usePropertyImages()
const { displayNotification } = useToastNotifications()

const imageLoading = ref(true)
const guidelines = ref([
  { label: 'main_image_guideline_1', isPositive: true },
  { label: 'main_image_guideline_2', isPositive: true },
  { label: 'main_image_guideline_3', isPositive: false },
  { label: 'main_image_guideline_4', isPositive: false },
])

const props = defineProps({
  isOpen: Boolean,
  imageToSetAsMain: {
    type: Object,
    default: null,
  },
})

watch(
  () => props.imageToSetAsMain?.imagePath,
  () => {
    imageLoading.value = true
  }
)

const mainImage = () => {
  if (!props.imageToSetAsMain?.imageId) return

  setIsSettingMainImage(true)
  setMainImage(
    {
      accommodationId: selectedAccommodationId.value,
      imageId: props.imageToSetAsMain.imageId,
      imagePath: props.imageToSetAsMain.imagePath,
      organisationId: selectedOrganisationId.value,
    },
    {
      onSuccess: data => {
        if (data.status === 200) {
          appAnalytics.track(appAnalytics.events.PD_NEW_MAIN_IMAGE_SET, {
            item_id: selectedAccommodationId.value,
          })
          setIsSettingMainImage(false)
          displayNotification({
            message: 'app_images_successfully_set_main_image',
            isTranslationKey: true,
            isHtml: false,
            type: 'success',
          })

          closeModal()
        }
      },
      onError: () => {
        setIsSettingMainImage(false)
        displayNotification({
          message: 'something_went_wrong',
          isTranslationKey: true,
          isHtml: false,
          type: 'error',
        })
      },
    }
  )
}
</script>
